import {FC, ReactNode, useCallback, useRef, useState} from "react";
import {Button} from "@interstate/components/Button";
import {PencilSquareIcon} from "@interstate/components/Icons";
import {Spinner} from "@interstate/components/Spinner";

export interface InlineEditorProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  editMode: "inline" | "modal";
  renderInput: (value: string, onChange: (value: string) => void) => ReactNode;
  renderDisplay: (value: string) => ReactNode;
  loading?: boolean;
  error?: string;
  autoSave?: boolean;
  hideEditButton?: boolean;
  isEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

export const InlineEditor: FC<InlineEditorProps> = ({
  value,
  onSave,
  editMode,
  renderInput,
  renderDisplay,
  loading = false,
  autoSave = false,
  hideEditButton = false,
  isEditing: controlledEditing,
  onEditingChange
}) => {
  const [internalEditing, setInternalEditing] = useState(false);
  const editing = controlledEditing ?? internalEditing;
  const setEditing = (val: boolean) => {
    setInternalEditing(val);
    onEditingChange?.(val);
  };
  const [editValue, setEditValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleEdit = () => {
    setEditValue(value);
    setError(null);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = useCallback(async (saveValue?: string) => {
    const val = saveValue ?? editValue;
    if (val === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(val);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [editValue, value, onSave]);

  const handleBlur = () => {
    if (!autoSave || saving) return;
    blurTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 150);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancel();
    } else if (e.key === "Enter" && autoSave) {
      handleSave();
    }
  };

  if (!editing) {
    if (hideEditButton) {
      return <div data-testid="inline-editor">{renderDisplay(value)}</div>;
    }
    return (
      <div data-testid="inline-editor" style={{display: "flex", alignItems: "flex-start", gap: "8px"}}>
        <div style={{flex: 1}}>{renderDisplay(value)}</div>
        <button
          data-testid="inline-editor-edit-button"
          onClick={handleEdit}
          aria-label={`Edit ${editMode}`}
          style={{background: "none", border: "none", cursor: "pointer", padding: "4px", flexShrink: 0}}
        >
          <PencilSquareIcon />
        </button>
      </div>
    );
  }

  const inputContent = (
    <div style={{position: "relative"}}>
      <fieldset disabled={saving} style={{border: "none", padding: 0, margin: 0, opacity: saving ? 0.5 : 1}}>
        {renderInput(editValue, setEditValue)}
      </fieldset>
      {saving && (
        <div
          data-testid="inline-editor-saving"
          style={{
            position: "absolute",
            top: "50%",
            right: "8px",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center"
          }}
        >
          <span style={{transform: "scale(0.5)", display: "inline-flex"}}>
            <Spinner data-testid="inline-editor-spinner" />
          </span>
        </div>
      )}
    </div>
  );

  if (autoSave) {
    return (
      <div
        data-testid="inline-editor-form"
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      >
        {inputContent}
        {error && (
          <div data-testid="inline-editor-error" style={{color: "red", fontSize: "12px", marginTop: "4px"}}>
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="inline-editor-form">
      {inputContent}
      {error && (
        <div data-testid="inline-editor-error" style={{color: "red", fontSize: "12px", marginTop: "4px"}}>
          {error}
        </div>
      )}
      <div style={{display: "flex", gap: "8px", marginTop: "8px"}}>
        <Button
          data-testid="inline-editor-save-button"
          size="small"
          onClick={() => handleSave()}
          disabled={saving || loading}
        >
          Save
        </Button>
        <Button
          data-testid="inline-editor-cancel-button"
          size="small"
          buttonStyle="secondary"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
